import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Share as ShareIcon,
  Analytics as AnalyticsIcon,
  Home as HomeIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { notifyPaymentProcessed, notifyPaymentFailed } from '../../utils/notificationHelpers';

interface PaymentData {
  id: string;
  amount: number;
  status: string;
  channels: Array<{
    channelName: string;
    tier: number;
    price: number;
    status: string;
  }>;
  createdAt: string;
  paidAt: string;
}

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    if (paymentId) {
      fetchPaymentData();
    } else {
      setError('No payment ID provided');
      setLoading(false);
    }
  }, [paymentId]);

  const fetchPaymentData = async () => {
    try {
      const response = await fetch(`/api/payments/status/${paymentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setPaymentData(data.payment);
        // Create an in-app notification based on payment status
        if (data.payment?.status === 'completed') {
          notifyPaymentProcessed(data.payment.amount, data.payment.channels?.length || 0).catch(
            (err) => console.error('Failed to send payment processed notification', err)
          );
        } else if (data.payment?.status && data.payment.status !== 'completed') {
          notifyPaymentFailed(
            data.payment.amount,
            `Status: ${data.payment.status}`
          ).catch((err) => console.error('Failed to send payment failed notification', err));
        }
      } else {
        setError(data.message || 'Failed to fetch payment data');
      }
    } catch (error) {
      setError('Failed to fetch payment data');
    } finally {
      setLoading(false);
    }
  };

  const getTierName = (tier: number) => {
    const names = {
      1: 'Tier 1 (Major National)',
      2: 'Tier 2 (Industry Specific)',
      3: 'Tier 3 (Regional)',
      4: 'Tier 4+ (Niche)'
    };
    return names[tier as keyof typeof names] || `Tier ${tier}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" style={{ padding: '32px 0', textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginTop: '16px' }}>
          Loading payment details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" style={{ padding: '32px 0' }}>
        <Alert severity="error" style={{ marginBottom: '16px' }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          startIcon={<HomeIcon />}
        >
          Go Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" style={{ padding: '32px 0' }}>
      <Paper elevation={3} style={{ padding: '32px', textAlign: 'center' }}>
        <CheckCircleIcon style={{ fontSize: 80, color: '#4caf50', marginBottom: '16px' }} />
        
        <Typography variant="h4" gutterBottom color="success.main">
          Payment Successful!
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          Thank you for your payment, {user?.firstName}!
        </Typography>
        
        <Typography variant="body1" color="textSecondary" paragraph>
          Your press release has been submitted for distribution to {paymentData?.channels.length} channels.
          You will receive email notifications as your PR gets published.
        </Typography>

        {paymentData && (
          <Card style={{ marginTop: '24px', textAlign: 'left' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <ReceiptIcon style={{ marginRight: '8px' }} />
                <Typography variant="h6">Payment Details</Typography>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <Typography variant="body2" color="textSecondary">Payment ID:</Typography>
                <Typography variant="body2" fontFamily="monospace">{paymentData.id}</Typography>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <Typography variant="body2" color="textSecondary">Amount Paid:</Typography>
                <Typography variant="h6" color="primary">
                  ${paymentData.amount.toLocaleString()}
                </Typography>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <Typography variant="body2" color="textSecondary">Status:</Typography>
                <Chip 
                  label={paymentData.status.toUpperCase()} 
                  color={getStatusColor(paymentData.status) as any}
                  size="small"
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                <Typography variant="body2" color="textSecondary">Paid At:</Typography>
                <Typography variant="body2">
                  {new Date(paymentData.paidAt).toLocaleString()}
                </Typography>
              </div>

              <Divider style={{ margin: '16px 0' }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Distribution Channels ({paymentData.channels.length})
              </Typography>
              
              <List dense>
                {paymentData.channels.map((channel, index) => (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      <ShareIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={channel.channelName}
                      secondary={
                        <div>
                          <Typography variant="body2" color="textSecondary">
                            {getTierName(channel.tier)} • ${channel.price.toLocaleString()}
                          </Typography>
                          <Chip 
                            label={channel.status} 
                            size="small" 
                            color={channel.status === 'pending' ? 'warning' : 'success'}
                            style={{ marginTop: '4px' }}
                          />
                        </div>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        <div style={{ marginTop: '32px', display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => navigate('/analytics')}
            startIcon={<AnalyticsIcon />}
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
          >
            View Analytics
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => navigate('/pr-library')}
            startIcon={<ReceiptIcon />}
          >
            View PR Library
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            startIcon={<HomeIcon />}
          >
            Go Home
          </Button>
        </div>

        <Alert severity="info" style={{ marginTop: '24px' }}>
          <EmailIcon style={{ marginRight: '8px' }} />
          You will receive email notifications as your press release gets published on each channel.
          Check your analytics dashboard for real-time performance metrics.
        </Alert>
      </Paper>
    </Container>
  );
};

export default PaymentSuccess;
